import React from 'react';
import styled, { ThemeConsumer } from 'styled-components';

// Components
import LoadingDriveAssistIcons from '../../../../../../common/src/legacy/LoadingDriveAssistIcons';
import { CheckBox_v2 as CheckBox } from 'wg-fe-ui';
import { mq } from '../../../../../../common/src/utils/styledComponentsUtils';

interface Props {
  title: string;
  description?: string;
  type: string;
  value: boolean;
  onSelected: (arg0: string) => void;
}

const CheckBoxWithIcon: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  description,
  type,
  value,
  onSelected,
  ...otherProps
}) => {
  return (
    <ThemeConsumer>
      {(theme) => (
        <Wrapper
          onClick={() => onSelected(type)}
          checked={value}
          data-test-id="ProdFac_CheckboxWithIcon"
          {...otherProps}
        >
          <BoundingBox data-test-id="ProdFac_CheckboxWithIcon_Icon">
            <Icon type={type} className={type} colors={theme} checked={value} />
          </BoundingBox>
          <TitleWrapper data-test-id="ProdFac_CheckboxWithIcon_TitleWrapper">
            <Title
              checked={value}
              data-test-id="ProdFac_CheckboxWithIcon_Title"
            >
              {title}
            </Title>
            {description && (
              <Description data-test-id="ProdFac_CheckboxWithIcon_Description">
                {description}
              </Description>
            )}
          </TitleWrapper>
          <StyledCheckBox
            checked={value}
            onClick={(e: React.MouseEvent) => e.preventDefault()}
            data-test-id="ProdFac_CheckboxWithIcon_Checkbox"
          />
        </Wrapper>
      )}
    </ThemeConsumer>
  );
};

const StyledCheckBox = styled(CheckBox)<{ checked: boolean }>`
  display: block;
  div {
    border: 2px solid
      ${({ theme, checked }) =>
        checked ? theme.brand.primary : theme.gray[100]};
    width: 2.5rem;
    height: 2.5rem;
    margin: 0;
  }
`;

const Icon = styled(LoadingDriveAssistIcons)`
  min-width: 100%;
  height: 100%;
`;

const BoundingBox = styled.div`
  width: 4rem;
  height: 3.5rem;
`;

const Title = styled.h4<{ checked: boolean }>`
  font-size: 1.4rem;
  font-weight: 400;
  line-height: 120%;
  color: ${({ theme, checked }) =>
    checked ? theme.brand.primary : theme.typo.title};

  ${mq.mobileM} {
    font-size: 1.6rem;
  }
`;

const Description = styled.p`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.typo.subTitle};
`;

const TitleWrapper = styled.div`
  width: 100%;
`;

const Wrapper = styled.div<{ checked: boolean }>`
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.8rem 1.6rem;
  border: 1px solid
    ${({ theme, checked }) => (checked ? theme.brand.primary : theme.gray[100])};
  border-radius: 8px;

  > * {
    margin-right: 1.6rem;

    &:last-child {
      margin-right: 0;
    }
  }

  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.1),
      0 0 1px rgba(0, 0, 0, 0.06);
  }
`;

export default CheckBoxWithIcon;
